import { CircleCheck as TabIconCircleCheck, Clock as TabIconClock } from "lucide-react";
import { getDeferredOutAmounts, ClientLateSettledTransaction } from "./clientEarningsData";
import { useLocale } from "../../../../hooks/useLocale";
import { msg } from "../../../../locales/translate.ts";
import { Select } from "@ui/select/Select";
import { saveAs } from "file-saver";
import { ArrowRight, BookOpenCheck, CalendarDays, CircleAlert, CircleCheck, Clock, FileDown, Hourglass, Info, LoaderCircle, OctagonAlert, RotateCw, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Trans, useTranslation } from "react-i18next";
import { GD } from "src/GD";
import type { IPaymentSession } from "src/types/types";
import { S_STATEMENT_POPUP } from "src/view/reports/StatementPopup";
import { formatProfitAmount } from "src/view/statView/accountantData";
import { AccountingCard as Card, KpiLabel, ReserveRefreshButton } from "src/view/statView/styled";
import styled from "styled-components";
import { AdaptiveTabBar } from "src/view/conponents/AdaptiveTabBar";
import { getClientEarningsRequest, parseClientEarnings, type ClientEarningsData, type ClientEarningsMetrics, type ClientUnsettledTransaction } from "./clientEarningsData";

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	flex-wrap: wrap;
`;

const SelectedPeriod = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px 20px;
	margin-top: 16px;
	padding: 12px 14px;
	border-left: 3px solid #344054;
	background: #f3f6fa;
	color: #1d2939;
	.period-label { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; }
	.period-range { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; font-size: 20px; font-weight: 700; font-variant-numeric: tabular-nums; }
	time { white-space: nowrap; }
`;

// Rows follow the agreed mockup (7 + 3 on Paid); the value size shrinks with the card so seven columns still fit.
const Kpis = styled.div`
	container-type: inline-size;
	margin-top: 12px;
	border: 1px solid #e5e7eb;
	border-radius: 10px;
	overflow: hidden;
	dl { display: grid; grid-template-columns: repeat(var(--columns), minmax(0, 1fr)); margin: 0 -1px -1px 0; }
	dl > div { display: flex; flex-direction: column; gap: 10px; min-width: 0; padding: 18px 20px; border-right: 1px solid #e5e7eb; border-bottom: 1px solid #e5e7eb; }
	dt { color: #57534e; font-size: 13px; line-height: 1.4; }
	dd { margin: 0; color: #141413; font-size: clamp(16px, calc(1.98cqi - 4.4px), 21px); font-weight: 600; line-height: 1.35; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
	.rolling-taken { background: #f7f4fe; }
	.rolling-taken dt, .rolling-taken dd { color: #6d28d9; }
	.rolling-released { background: #eff6ff; }
	.rolling-released dt, .rolling-released dd { color: #1d4ed8; }
	.chargebacks { background: #fef2f2; }
	.chargebacks dt, .chargebacks dd { color: #b91c1c; }
	dd.net { color: #15803d; }
	@container (max-width: 960px) {
		dl { display: flex; flex-wrap: wrap; }
		dl > div { flex: 1 1 170px; padding: 16px 18px; }
		dd { font-size: 19px; }
	}
`;

const SectionHeading = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-top: 20px;
	color: #344054;
	font-size: 13px;
	font-weight: 600;
`;

const SectionTitle = styled.header`
	display: flex;
	align-items: baseline;
	flex-wrap: wrap;
	gap: 4px 12px;
	margin-top: 28px;
	h2 { margin: 0; color: #141413; font-size: 19px; font-weight: 600; }
	span { color: #57534e; font-size: 14px; }
`;

const RoundingNote = styled.p`
	display: flex;
	align-items: flex-start;
	gap: 9px;
	margin: 10px 0 0;
	padding: 0 2px;
	color: #78716c;
	font-size: 12.5px;
	line-height: 1.5;
	svg { flex-shrink: 0; margin-top: 2px; }
`;

// A period that runs past the last statement: nothing is wrong, those days just have no settlement and no calculation yet.
const OpenPeriod = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	margin-top: 24px;
	padding: 16px 22px;
	border: 1px solid #e7e5e4;
	border-left: 4px solid #a8a29e;
	border-radius: 10px;
	background: #fafaf9;
	color: #44403c;
	svg { flex-shrink: 0; margin-top: 2px; color: #57534e; }
	flex-wrap: wrap;
	.tail-text { display: flex; flex-direction: column; gap: 4px; flex: 1 1 320px; min-width: 0; }
	strong { color: #292524; font-size: 15px; font-weight: 600; }
	span { font-size: 13px; line-height: 1.5; }
	.tail-note { color: #78716c; font-size: 12px; }
	.tail-amount { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; margin-left: auto; }
	.tail-amount b { color: #292524; font-size: 24px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.tail-amount span { color: #78716c; font-size: 12px; }
`;

// Both sides of the settlement shift as a thin caption over the report-days table: a day's report holds the previous day's payments.
const ShiftCaption = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 10px 26px;
	margin-top: 12px;
	padding: 12px 18px;
	border: 1px solid #c7d2fe;
	border-left: 3px solid #4f46e5;
	border-radius: 8px;
	background: #eef2ff;
	color: #3730a3;
	font-size: 12.5px;
	line-height: 1.5;
	.shift-text { flex: 1 1 300px; min-width: 0; }
	.shift-sides { display: flex; flex-wrap: wrap; gap: 8px 22px; }
	.shift-sides > span { white-space: nowrap; }
	strong { font-weight: 600; font-variant-numeric: tabular-nums; }
	em { color: #4338ca; font-style: normal; }
`;

const Reconciled = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	margin-top: 24px;
	padding: 18px 24px;
	border: 1px solid #bbf7d0;
	border-radius: 10px;
	background: #f0fdf4;
	color: #166534;
	svg { flex-shrink: 0; margin-top: 2px; color: #15803d; }
	strong { display: block; margin-bottom: 6px; font-size: 15px; font-weight: 600; }
	p { margin: 0; font-size: 13px; line-height: 1.8; font-variant-numeric: tabular-nums; }
	em { color: #4d7c57; font-style: normal; }
	b { font-weight: 600; }
`;

const OverviewTurnover = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 8px 24px;
	margin-top: 20px;
	padding: 20px 24px;
	border: 1px solid #e5e7eb;
	border-left: 4px solid #141413;
	border-radius: 10px;
	background: #fafaf9;
	.overview-label { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
	.overview-title { color: #57534e; font-size: 13px; font-weight: 500; letter-spacing: .01em; }
	.overview-hint { color: #78716c; font-size: 12px; line-height: 1.5; }
	.overview-amount { color: #141413; font-size: 30px; font-weight: 600; letter-spacing: -.01em; font-variant-numeric: tabular-nums; white-space: nowrap; }
`;

// Card colours follow the agreed Deferred mockup: amber Out, slate In, red Not settled, stone for figures that do not reconcile.
const DeferredSection = styled.section`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-top: 28px;
	color: #141413;
	h3 { margin: 0; color: #141413; font-size: 19px; font-weight: 600; }
	.deferred-grid { display: flex; flex-wrap: wrap; align-items: stretch; gap: 20px; }
	.deferred-card {
		--title: #334155; --meta: #475569; --text: #334155; --line: #e2e8f0;
		display: flex; flex-direction: column; gap: 14px; flex: 1 1 320px; min-width: 0; box-sizing: border-box;
		padding: 22px 24px; border: 1px solid var(--line); border-radius: 12px; background: #f8fafc; color: var(--text);
	}
	.deferred-card.out { --title: #78350f; --meta: #92400e; --text: #78350f; --line: #fde68a; background: #fffbeb; }
	.deferred-card.not-billed.has-unbilled { --title: #b42318; --meta: #b42318; --text: #b42318; --line: #fecdca; border: 1px solid #fda29b; border-radius: 12px; background: #fff; box-shadow: 0 1px 2px rgba(16,24,40,.05); }
	.deferred-card.not-billed { background: #fff; border-color: #d0d5dd; box-shadow: 0 1px 2px rgba(16,24,40,.05); }
	.not-billed .deferred-entry { color: #b42318; background: #fef3f2; }
	.not-billed .deferred-entry-id { color: #b42318; }
	.deferred-card.unsettled { --title: #991b1b; --meta: #b91c1c; --text: #991b1b; --line: #fca5a5; border: 2px solid #dc2626; background: #fef2f2; }
	.deferred-card.muted { --title: #57534e; --meta: #78716c; --text: #57534e; --line: #e7e5e4; background: #fafaf9; }
	header { display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: 4px 16px; }
	h4 { display: flex; align-items: center; gap: 8px; margin: 0; color: var(--title); font-size: 15px; font-weight: 600; }
	.unsettled h4 { font-weight: 700; letter-spacing: .02em; }
	.unsettled h4 svg { flex-shrink: 0; color: #dc2626; }
	.deferred-count { color: var(--meta); font-size: 13px; white-space: nowrap; }
	.unsettled .deferred-count { font-weight: 600; }
	p { margin: 0; font-size: 13px; line-height: 1.5; }
	p strong { font-weight: 600; }
	dl { display: flex; flex-direction: column; gap: 6px; margin: 4px 0 0; }
	dl > div { display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: 4px 16px; }
	dt { color: var(--meta); font-size: 13px; }
	dd { margin: 0; color: var(--title); font-size: 26px; font-weight: 600; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
	.unsettled dd { font-weight: 700; }
	.deferred-card.late { --title: #92400e; --meta: #b45309; --text: #78350f; --line: #fde68a; border: 2px solid #d97706; background: #fffbeb; }
	.deferred-card.open { --title: #334155; --meta: #64748b; --text: #475569; --line: #e2e8f0; border: 1px solid #cbd5e1; background: #f8fafc; }
	.not-settled h4 { font-weight: 600; }
	.late h4 svg { flex-shrink: 0; color: #d97706; }
	.unsettled .deferred-empty { border-color: #fca5a5; color: #b91c1c; }
	.settlement-rows dt { flex: 1 1 150px; color: var(--meta); font-size: 13px; line-height: 1.45; }
	.settlement-rows dd { display: flex; flex-direction: column; align-items: flex-end; font-size: 22px; font-weight: 700; }
	.settlement-rows.boxed { gap: 8px; }
	.settlement-rows.boxed > div { padding: 10px 12px; border-radius: 8px; }
	.settlement-rows.boxed dd { font-size: 16px; }
	.settlement-rows.boxed .late { background: #fffbeb; }
	.settlement-rows.boxed .lost { background: #fee2e2; }
	.settlement-rows.boxed .open { background: #f1f5f9; }
	.settlement-rows .late dt, .settlement-rows .late dd { color: #92400e; }
	.settlement-rows .lost dt, .settlement-rows .lost dd { color: #991b1b; }
	.settlement-rows .open dt, .settlement-rows .open dd { color: #475569; }
	.deferred-entry.late .deferred-entry-meta { color: #b45309; }
	.deferred-entry.late .deferred-entry-amount { color: #92400e; }
	.deferred-entry.lost .deferred-entry-meta { color: #b91c1c; }
	.deferred-entry.lost .deferred-entry-amount { color: #991b1b; }
	.deferred-entry.open .deferred-entry-meta, .deferred-entry.open .deferred-entry-amount { color: #64748b; }
	.not-settled .recent { display: flex; flex-direction: column; gap: 8px; margin: auto -24px -22px; padding: 14px 24px 16px; border-top: 1px solid var(--line); background: #fafaf9; color: #44403c; border-radius: 0 0 10px 10px; }
	/* The strip runs to the card edges, so its bottom corners follow the card radius minus the border (2px red, 1px calm). */
	.not-settled.in .recent { border-radius: 0 0 11px 11px; }
	.not-settled .recent strong { color: #44403c; font-size: 13px; font-weight: 600; }
	.not-settled .recent .deferred-count { color: #78716c; font-weight: 400; }
	.recent-row { display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: 4px 14px; color: #57534e; font-size: 12px; line-height: 1.45; }
	.recent-row b { display: flex; flex-direction: column; align-items: flex-end; color: #44403c; font-size: 18px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.recent details summary { width: fit-content; color: #57534e; font-size: 12px; cursor: pointer; }
	.recent details .deferred-list { margin-top: 8px; max-height: 260px; overflow-y: auto; }
	.settlement-groups dd { display: flex; flex-direction: column; align-items: flex-end; }
	hr { width: 100%; height: 1px; margin: 0; border: 0; background: var(--line); }
	.deferred-pill { padding: 8px 12px; border-radius: 8px; background: #f5f5f4; color: #57534e; font-size: 12px; line-height: 1.45; }
	.unsettled .deferred-pill { background: #fee2e2; color: #991b1b; }
	.deferred-empty { display: flex; flex: 0 0 auto; align-items: center; justify-content: center; min-height: 64px; box-sizing: border-box; padding: 16px; border: 1px dashed #cbd5e1; border-radius: 8px; color: #64748b; font-size: 14px; text-align: center; }
	.muted .deferred-empty { border-color: #d6d3d1; color: #78716c; }
	.deferred-list { display: flex; flex-direction: column; gap: 4px; list-style: none; margin: 0; padding: 0; }
	.not-settled .deferred-list { max-height: 360px; margin-right: -8px; padding-right: 8px; overflow-y: auto; }
	.deferred-entry { display: flex; flex-direction: column; gap: 4px; width: 100%; padding: 6px 4px; border: 0; border-radius: 6px; background: transparent; color: var(--text); font: inherit; text-align: left; cursor: pointer; }
	.deferred-entry:hover { background: rgba(255,255,255,.7); }
	.deferred-entry:focus-visible { outline: 2px solid #6374d9; outline-offset: 2px; }
	.deferred-entry-row { display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: 2px 12px; font-size: 13px; line-height: 1.5; }
	.deferred-entry-id { color: #1d4ed8; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
	.unsettled .deferred-entry-id { font-size: 13px; }
	.deferred-entry:hover .deferred-entry-id { text-decoration: underline; text-underline-offset: 3px; }
	.deferred-entry-meta { color: var(--meta); font-size: 12px; }
	.deferred-entry-amount { display: flex; flex-wrap: wrap; gap: 4px 12px; color: var(--title); font-size: 15px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.deferred-more { color: var(--meta); font-size: 12px; }
	.deferred-alert { display: flex; align-items: flex-start; gap: 12px; padding: 16px 20px; border: 2px solid #dc2626; border-radius: 10px; background: #fef2f2; color: #991b1b; font-size: 13px; line-height: 1.5; }
	.deferred-alert svg { flex-shrink: 0; margin-top: 1px; color: #b91c1c; }
	.deferred-alert strong { display: block; margin-bottom: 4px; font-size: 15px; font-weight: 700; }
	.not-paid-out { display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: 8px 20px; padding: 16px 24px; border: 1px solid #fca5a5; border-radius: 10px; background: #fef2f2; }
	.not-paid-out-label { color: #991b1b; font-size: 15px; font-weight: 600; }
	.not-paid-out-value { color: #b91c1c; font-size: 14px; font-variant-numeric: tabular-nums; text-align: right; }
	.not-paid-out-value strong { color: #991b1b; font-size: 20px; font-weight: 700; white-space: nowrap; }
	.not-paid-out.calm { border-color: #e2e8f0; background: #f8fafc; }
	.not-paid-out.calm .not-paid-out-label, .not-paid-out.calm .not-paid-out-value strong { color: #334155; }
	.not-paid-out.calm .not-paid-out-value { color: #475569; }
	.not-paid-out.withheld { display: block; border: 1px dashed #d6d3d1; background: #fafaf9; color: #57534e; font-size: 14px; line-height: 1.5; }
	.not-paid-out.withheld strong { color: #44403c; font-weight: 600; }
	.deferred-note { display: flex; align-items: flex-start; gap: 10px; padding: 0 2px; color: #57534e; overflow-wrap: anywhere; }
	.deferred-note svg { flex-shrink: 0; margin-top: 1px; color: #78716c; }
	.deferred-note.reconciled, .deferred-note.reconciled svg { color: #15803d; }
`;

const TableScroll = styled.div`
	max-width: 100%;
	overflow-x: auto;
	margin-top: 10px;
	border: 1px solid #e7ebf1;
	border-radius: 8px;
	table { width: 100%; min-width: 960px; border-collapse: collapse; font-size: 13px; }
	th, td { padding: 10px 12px; border-bottom: 1px solid #edf0f4; text-align: right; white-space: nowrap; }
	th { background: #fafbfc; color: #667085; font-size: 12px; font-weight: 500; }
	th:first-child, td:first-child { text-align: left; }
	td { color: #475467; font-variant-numeric: tabular-nums; }
	td.rolling-taken { color: #6d28d9; background: #f7f4fe; border-bottom-color: #e9e1fb; }
	td.rolling-released { color: #1d4ed8; background: #eff6ff; border-bottom-color: #dbe7fb; }
	td.chargebacks { color: #b91c1c; background: #fef2f2; border-bottom-color: #f7dcdc; }
	td.rolling-taken.zero { color: #a78bfa; }
	td.rolling-released.zero { color: #93c5fd; }
	td.chargebacks.zero { color: #fca5a5; }
	td:first-child { min-width: 190px; color: #1d2939; font-weight: 500; white-space: normal; }
	tbody tr:last-child td { border-bottom: 0; }
	tr.muted td { color: #78716c; }
	tr.total td { font-weight: 600; }
	tr.total td:not([class]) { background: #fafaf9; color: #1d2939; }
	.row-hint { color: #a8a29e; font-weight: 400; }
	tbody tr:hover { background: #f8fafc; }
	tbody tr[data-clickable] { cursor: pointer; }
	tbody tr[data-clickable]:hover, tbody tr[data-clickable]:focus-within { background: #eef4fb; }
	.statement-open { padding: 0; border: 0; background: transparent; color: #25638e; font: inherit; cursor: pointer; text-decoration: none; text-underline-offset: 3px; }
	.statement-open:hover { text-decoration: underline; }
	.statement-open:focus-visible { outline: 2px solid #6374d9; outline-offset: 4px; border-radius: 2px; }
`;

const StatusBadge = styled.span<{ $partial?: boolean }>`
	display: inline-flex;
	padding: 3px 7px;
	border-radius: 5px;
	background: ${props => props.$partial ? "#fff4e5" : "#edf8f1"};
	color: ${props => props.$partial ? "#925f00" : "#157347"};
	font-size: 11px;
	font-weight: 600;
`;

const Reconciliation = styled.div<{ $warning: boolean }>`
	margin-top: 18px;
	padding: 12px 14px;
	border-left: 3px solid ${props => props.$warning ? "#d99a00" : "#27945b"};
	background: ${props => props.$warning ? "#fff9ed" : "#f0faf4"};
	color: ${props => props.$warning ? "#6f4b00" : "#17603a"};
	font-size: 12px;
	line-height: 1.5;
	strong { display: block; margin-bottom: 4px; font-size: 13px; }
	ul { margin: 5px 0 0; padding-left: 18px; }
`;

const nextDay = (day: string) => {
	const date = new Date(`${day}T00:00:00Z`);
	date.setUTCDate(date.getUTCDate() + 1);
	return date.toISOString().slice(0, 10);
};

// Server timestamps without an offset are UTC.
const parseServerDate = (value: string) => {
	const iso = value.trim().replace(" ", "T");
	const date = new Date(iso.includes("T") && !/(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? `${iso}Z` : iso);
	return Number.isNaN(date.getTime()) ? undefined : date;
};

interface ClientEarningsCardProps {
	clientId: number;
	clientName?: string;
	projectId: number;
	userId: number;
	from: string;
	to: string;
}

export const ClientEarningsCard = ({ clientId, clientName, projectId, userId, from, to }: ClientEarningsCardProps) => {
	useLocale();
	const { t, i18n } = useTranslation();
	const [view, setView] = useState<"accrued" | "paid">("paid");
	const [selectedCurrency, setSelectedCurrency] = useState("EUR");
	const [attempt, setAttempt] = useState(0);
	const [state, setState] = useState<{ busy: boolean; data?: ClientEarningsData; error?: string }>({ busy: true });
	const [pdfBusy, setPdfBusy] = useState(false);
	const [pdfError, setPdfError] = useState(false);
	const exportVersion = useRef(0);

	useEffect(() => {
		let cancelled = false;
		exportVersion.current += 1;
		setPdfBusy(false);
		setPdfError(false);
		const subscription = GD.S_AUTH_LOG_OUT.subscribe(() => {
			cancelled = true;
			exportVersion.current += 1;
			setPdfBusy(false);
			setPdfError(false);
			setState({ busy: false });
		});
		setState({ busy: true });
		const load = async () => {
			const checkContext = async () => {
				const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
				if (cancelled || profile?.id !== userId || project?.id !== projectId) throw new Error("Context changed");
			};
			try {
				await checkContext();
				const response = await GD.REQ_API.request({ ...getClientEarningsRequest(clientId, from, to), expectedProjectId: projectId });
				await checkContext();
				if (response?.error || response?.data === undefined || response.data === null) throw new Error(msg("earningsRequestFailed"));
				const data = parseClientEarnings(response.data, clientId, from, to);
				if (!data) throw new Error(msg("unexpectedEarningsResponse"));
				if (!cancelled) setState({ busy: false, data });
			} catch {
				if (!cancelled) setState({ busy: false, error: t("accountant.earningsError") });
			}
		};
		void load();
		return () => { cancelled = true; exportVersion.current += 1; GD.S_AUTH_LOG_OUT.unsubscribe(subscription); };
	}, [clientId, projectId, userId, from, to, attempt, t]);

	const data = state.data;
	const section = data?.[view];
	const currencies = section ? Object.keys(section.byCurrency) : [];
	const currency = currencies.includes(selectedCurrency) ? selectedCurrency : currencies[0] ?? "EUR";
	const metrics = section?.byCurrency[currency];
	const locale = t("accountant.locale");
	const money = (amount: number) => formatProfitAmount(amount, currency, locale);
	const plain = (amount: number) => new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(amount);
	const kpis: Array<{ label: string; value: number; net?: boolean; tone?: string }> = metrics ? [
		{ label: t("accountant.payableTurnover"), value: metrics.turnover },
		{ label: t("accountant.fees"), value: metrics.fees },
		{ label: t("accountant.rollingTaken"), value: metrics.rollingTaken, tone: "rolling-taken" },
		{ label: t("accountant.rollingReleased"), value: metrics.rollingReleased, tone: "rolling-released" },
		{ label: t("accountant.refunds"), value: metrics.refunds },
		{ label: t("accountant.chargebacks"), value: metrics.chargebacks, tone: "chargebacks" },
		...(view === "paid" ? [
			{ label: t("accountant.withdrawal"), value: metrics.withdrawal ?? 0 },
			{ label: t("accountant.setup"), value: metrics.setup ?? 0 },
			{ label: t("accountant.monthly"), value: metrics.monthly ?? 0 },
			...(metrics.oct ? [{ label: t("accountant.oct"), value: metrics.oct }] : []),
		] : []),
		{ label: t("accountant.net"), value: metrics.net, net: true },
	] : [];
	const channels = section?.channels.filter(channel => channel.currencies[currency]) ?? [];
	const statements = data?.paid.statements.filter(statement => statement.net[currency] !== undefined) ?? [];
	// A zero amount per currency means nothing is pending.
	// An amount per currency billed by statements that are not completed yet (status "created").
	const pendingAmount = typeof data?.paid.pending[currency] === "number" ? data.paid.pending[currency] as number : 0;
	const byPeriod = view === "paid" ? data?.paid.byPeriod : undefined;
	const deferred = view === "paid" ? data?.paid.deferred : undefined;
	const notPaidOut = view === "paid" ? data?.paid.notPaidOut : undefined;
	const unsettled = view === "paid" ? data?.unsettled : undefined;
	const overview = view === "paid" ? data?.turnoverInRange?.[currency] : undefined;
	const deferredIn = deferred?.in[currency] ?? 0;
	const outAmounts = view === "paid" && data ? getDeferredOutAmounts(data.paid) : {};
	const deferredOut = outAmounts[currency] ?? 0;
	const lateSettled = view === "paid" ? data?.paid.lateSettled : undefined;
	const billedTransactions = lateSettled?.transactions.filter(transaction => transaction.billedBy !== null) ?? [];
	const notBilledTransactions = lateSettled?.transactions.filter(transaction => transaction.billedBy === null) ?? [];
	const notBilledAmount = notPaidOut?.notBilled?.[currency] ?? 0;
	const showNotBilled = !!notPaidOut?.notBilled || !!notBilledTransactions.length;
	const hasNotBilled = Object.values(notPaidOut?.notBilled ?? {}).some(amount => amount !== 0) || !!notBilledTransactions.length;
	const unsettledAmount = notPaidOut?.unsettled[currency] ?? unsettled?.byCurrency[currency]?.amount ?? 0;
	// Totals are shown only as the server reports them; nothing on this card is summed up from partial data.
	const notPaidOutTotal = notPaidOut?.total[currency];
	const unexplained = view === "paid" ? data?.reconciliation.turnoverUnexplained?.[currency] : undefined;
	// A mismatch means the deferred figures are incomplete, so their total would understate what was not paid out.
	const turnoverMismatch = unexplained !== undefined && Math.round(unexplained * 100) !== 0;
	const overviewTurnover = overview?.amount;
	// Unknown and pending can still be confirmed by the acquirer (late), failed and error never will (lost) — never merged into one figure.
	const settlementGroup = (status: string) => status === "failed" || status === "error" ? "lost" : "late";
	const amountsOf = (byCurrency: Record<string, { amount: number }>) => Object.entries(byCurrency)
		.map(([code, total]) => ({ code, amount: total.amount })).filter(item => item.amount !== 0).sort((a, b) => a.code.localeCompare(b.code));
	// One row per server status, exactly as by_status reports it — no figure on this card is added up in the browser.
	const notSettledRows = unsettled ? Object.entries(unsettled.byStatus)
		.map(([status, byCode]) => ({ status, kind: settlementGroup(status), amounts: amountsOf(byCode) }))
		.filter(row => row.amounts.length) : [];
	const hasLate = notSettledRows.some(row => row.kind === "late"), hasLost = notSettledRows.some(row => row.kind === "lost");
	const notSettledState = hasLost ? hasLate ? "mixed" : "lost" : hasLate ? "late" : "empty";
	// The period is open when the server says it is billed only through a day before its end; then nothing can be reconciled yet.
	const closedThrough = view === "paid" ? data?.closedThrough : undefined;
	const serverTail = view === "paid" ? data?.openTail : undefined;
	const periodOpen = !!serverTail || (!!closedThrough && closedThrough < to);
	const tailFrom = serverTail?.from ?? (closedThrough && closedThrough < to ? nextDay(closedThrough) : undefined);
	const tailAmount = serverTail?.byCurrency[currency];
	// NOT SETTLED split into stuck / recent exactly as the server reports it; the per-payment side comes from the server's age_days.
	const split = unsettled?.stuck && unsettled.recent ? { stuck: unsettled.stuck, recent: unsettled.recent } : undefined;
	const stuckAfter = unsettled?.stuckAfterDays;
	const isStuck = (transaction: ClientUnsettledTransaction) => transaction.ageDays !== undefined && stuckAfter !== undefined && transaction.ageDays >= stuckAfter;
	const stuckTransactions = split ? unsettled?.transactions.filter(isStuck) ?? [] : [];
	const recentTransactions = split ? unsettled?.transactions.filter(transaction => !isStuck(transaction)) ?? [] : [];
	const shortDay = (day: string) => new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" }).format(new Date(`${day}T00:00:00Z`));
	const dayRange = (start: string, end: string) => start === end ? shortDay(start) : `${shortDay(start)} – ${shortDay(end)}`;
	// The settlement shift is a pair: over a single day both sides are the whole volume, so either alone would read as a discrepancy.
	const shiftShown = view === "paid" && !!data?.reportedLater && !!data?.reportedEarlier;
	const reportedLater = shiftShown ? data?.reportedLater?.byCurrency[currency] ?? { amount: 0, count: 0 } : undefined;
	const reportedEarlier = shiftShown ? data?.reportedEarlier?.byCurrency[currency] ?? { amount: 0, count: 0 } : undefined;
	const deferredBeyond = deferred?.beyond[currency] ?? 0;
	const reportsUnexplained = view === "paid" ? data?.reconciliation.reportsUnexplained?.[currency] : undefined;
	const laterMismatch = reportsUnexplained !== undefined && Math.round(reportsUnexplained * 100) !== 0;
	// Only a non-null non-zero check is red, and never on an open period: null there means "cannot be computed yet", a grey state.
	const mismatch = !periodOpen && (turnoverMismatch || laterMismatch);
	const mismatchAmount = Math.abs((turnoverMismatch ? unexplained : reportsUnexplained) ?? 0);
	const reconciled = !periodOpen && unexplained !== undefined && !mismatch && !!metrics;
	const rounding = view === "paid" ? data?.reconciliation.documentsRounding?.[currency] : undefined;
	const insideTurnover = byPeriod?.inside[currency]?.turnover ?? 0;
	const zeroMetrics: ClientEarningsMetrics = { turnover: 0, fees: 0, rollingTaken: 0, rollingReleased: 0, refunds: 0, chargebacks: 0, net: 0 };
	// The channel rows are an attribution from the reports and add up to by_period.inside, so its server figures are the total row.
	const channelTotal = view === "paid" ? byPeriod?.inside[currency] : undefined;
	const formatTransactionDate = (value: string) => {
		const date = parseServerDate(value);
		return date ? `${new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }).format(date)} UTC` : value;
	};
	const statusLabel = (status: string) => {
		const label = t(`accountant.settlementStatus.${status}`, { defaultValue: status });
		return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
	};
	const renderUnsettledEntry = (transaction: ClientUnsettledTransaction | ClientLateSettledTransaction, tone: string) => {
		// The age comes from the server (UTC); responses without age_days simply show none.
		const days = transaction.ageDays;
		const status = transaction.status && t(`accountant.settlementStatus.${transaction.status}`, { defaultValue: transaction.status });
		const age = !("billedBy" in transaction) && tone !== "open" && settlementGroup(transaction.status) === "late" && days !== undefined ? t("accountant.unsettledAge", { count: days }) : "";
		// Ids stay strings; the preview takes the numeric session id only while it is exact.
		const sessionId = /^\d+$/.test(transaction.id) && Number.isSafeInteger(Number(transaction.id)) ? Number(transaction.id) : transaction.id;
		return <li key={transaction.id}>
			<button type="button" className={`deferred-entry ${tone}`} aria-label={t("accountant.openTransaction", { id: transaction.id })} onClick={() => GD.S_TRANSACTION_PREVIEW.invoke({ id: sessionId } as unknown as IPaymentSession)}>
				<span className="deferred-entry-row"><span className="deferred-entry-id">#{transaction.id}</span><span className="deferred-entry-amount">{formatProfitAmount(transaction.amount, transaction.currency, locale)}</span></span>
				<span className="deferred-entry-meta">{[transaction.createdAt && formatTransactionDate(transaction.createdAt), "billedBy" in transaction ? transaction.billedBy ? `${t("accountant.statement")} #${transaction.billedBy.id}` : t("accountant.notBilled") : status, age].filter(Boolean).join(" · ")}</span>
			</button>
		</li>;
	};
	const periodRows = byPeriod ? [
		{ key: "inside", label: t("accountant.paidInsidePeriod"), metrics: byPeriod.inside[currency] },
		{ key: "outside", label: t("accountant.paidOutsidePeriod"), metrics: byPeriod.outside[currency] },
	] : [];

	const downloadPdf = async () => {
		if (!data || state.busy || pdfBusy || data.clientId !== clientId || data.period.from !== from || data.period.to !== to) return;
		const version = exportVersion.current;
		const translate = i18n.getFixedT(i18n.resolvedLanguage ?? i18n.language);
		setPdfBusy(true);
		setPdfError(false);
		try {
			const { renderClientEarningsPDF } = await import("src/tools/pdf/ClientEarningsPDF");
			const blob = await renderClientEarningsPDF({ data, clientName, t: translate });
			if (version !== exportVersion.current) return;
			const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
			if (version !== exportVersion.current || profile?.id !== userId || project?.id !== projectId) return;
			saveAs(blob, `earnings_${data.clientId}_${data.period.from}_${data.period.to}.pdf`);
		} catch {
			if (version === exportVersion.current) setPdfError(true);
		} finally {
			if (version === exportVersion.current) setPdfBusy(false);
		}
	};

	// Zeros in the tinted columns are muted, so a real chargeback or reserve stands out.
	const tone = (name: string, value: number) => value ? name : `${name} zero`;
	const shiftDay = (day: string, offset: number) => {
		const date = new Date(`${day}T00:00:00`);
		date.setDate(date.getDate() + offset);
		return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(date);
	};
	const renderChannelMetrics = (channelMetrics: ClientEarningsMetrics) => <>
		<td>{money(channelMetrics.turnover)}</td><td>{money(channelMetrics.fees)}</td><td className={tone("rolling-taken", channelMetrics.rollingTaken)}>{money(channelMetrics.rollingTaken)}</td>
		<td className={tone("rolling-released", channelMetrics.rollingReleased)}>{money(channelMetrics.rollingReleased)}</td><td>{money(channelMetrics.refunds)}</td><td className={tone("chargebacks", channelMetrics.chargebacks)}>{money(channelMetrics.chargebacks)}</td><td>{money(channelMetrics.net)}</td>
	</>;

	return <Card style={{ height: "auto", minWidth: 0, gridColumn: "1 / -1" }} role="region" aria-label={t("accountant.earningsTitle")} aria-busy={state.busy}>
		<Header>
			<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
				<div style={{ padding: 10, borderRadius: 10, color: "#365f8c", background: "#edf4fa", display: "flex" }}><BookOpenCheck size={20} aria-hidden="true" /></div>
				<div style={{ fontSize: 14, fontWeight: 600, color: "#1a1d26" }}>{t("accountant.earningsTitle")}</div>
			</div>
			<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
				<ReserveRefreshButton $busy={pdfBusy} title={t(pdfBusy ? "accountant.pdfGenerating" : "accountant.exportEarningsPdf")} aria-label={t(pdfBusy ? "accountant.pdfGenerating" : "accountant.exportEarningsPdf")} aria-busy={pdfBusy} disabled={!data || state.busy || pdfBusy} onClick={() => void downloadPdf()}>{pdfBusy ? <LoaderCircle size={18} aria-hidden="true" /> : <FileDown size={18} aria-hidden="true" />}</ReserveRefreshButton>
				<ReserveRefreshButton $busy={state.busy} title={state.error ? t("accountant.retry") : t("accountant.refresh")} aria-label={state.error ? t("accountant.retry") : t("accountant.refresh")} disabled={state.busy || pdfBusy} onClick={() => setAttempt(value => value + 1)}><RotateCw size={16} aria-hidden="true" /></ReserveRefreshButton>
			</div>
		</Header>
		<SelectedPeriod aria-label={t("accountant.period")}>
			<span className="period-label"><CalendarDays size={18} aria-hidden="true" />{t("accountant.period")}</span>
			<div className="period-range"><time dateTime={from}>{from}</time><ArrowRight size={18} aria-hidden="true" /><time dateTime={to}>{to}</time></div>
		</SelectedPeriod>
		<div role="status" aria-live="polite">
			{state.busy && <KpiLabel style={{ display: "block", marginTop: 12 }}>{t("accountant.earningsLoading")}</KpiLabel>}
			{state.error && <div style={{ marginTop: 12, color: "#b42332", fontSize: 13 }}>{state.error}</div>}
		</div>
		{pdfError && <div role="alert" style={{ marginTop: 12, color: "#b42332", fontSize: 13 }}>{t("accountant.pdfError")}</div>}
		{data && <>
			<div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
				<AdaptiveTabBar label={t("accountant.earningsView")} sx={{ marginTop: "18px", flex: "1 1 200px" }} items={
					(["paid", "accrued"] as const).map(kind => ({
						id: kind, label: t(`accountant.${kind}`), icon: kind === "paid" ? <TabIconCircleCheck size={16} /> : <TabIconClock size={16} />, selected: view === kind, onSelect: () => setView(kind),
					}))
				} />
				{currencies.length > 1 && <Select size="small" value={currency} onChange={setSelectedCurrency} values={currencies} />}
			</div>
			{overview && <OverviewTurnover>
				<div className="overview-label">
					<span className="overview-title">{t("accountant.overviewTurnover")}</span>
					<span className="overview-hint">{from === to
						? t("accountant.overviewTurnoverDayHint", { payments: new Intl.NumberFormat(locale).format(overview.count), day: new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(new Date(`${from}T00:00:00`)) })
						: t("accountant.overviewTurnoverHint", { payments: new Intl.NumberFormat(locale).format(overview.count), from, to })}</span>
				</div>
				<span className="overview-amount">{money(overview.amount)}</span>
			</OverviewTurnover>}
			{view === "paid"
				? <SectionTitle><h2>{t("accountant.byDocuments")}</h2><span>{data.paid.statements.length === 1
					? t("accountant.byDocumentsSingle", { id: data.paid.statements[0].id, type: data.paid.statements[0].type, status: data.paid.statements[0].status })
					: t("accountant.byDocumentsHint", { count: data.paid.statements.length })}</span></SectionTitle>
				: <KpiLabel style={{ display: "block", marginTop: 20, color: "#57534e", fontSize: 14 }}>{t("accountant.daysCovered", { count: data.accrued.daysCovered })}</KpiLabel>}
			{kpis.length ? <Kpis>{(view === "paid" ? [kpis.slice(0, 7), kpis.slice(7)] : [kpis]).filter(row => row.length).map(row => <dl key={row[0].label} style={{ "--columns": row.length } as CSSProperties}>
				{row.map(item => <div key={item.label} className={item.tone}><dt>{item.label}</dt><dd className={item.net ? "net" : undefined}>{money(item.value)}</dd></div>)}
			</dl>)}</Kpis> : <KpiLabel style={{ display: "block", marginTop: 10 }}>{t("accountant.noActivity")}</KpiLabel>}
			{rounding !== undefined && Math.round(rounding * 100) !== 0 && <RoundingNote>
				<Info size={15} aria-hidden="true" />
				<span>{t("accountant.documentsRounding", { amount: `${rounding > 0 ? "+" : "−"}${plain(Math.abs(rounding))}`, count: data.paid.statements.length })}</span>
			</RoundingNote>}
			{periodOpen && <OpenPeriod role="status">
				<Clock size={20} aria-hidden="true" />
				<div className="tail-text">
					<strong>{closedThrough && tailFrom
						? t("accountant.openPeriod.title", { through: shortDay(closedThrough), days: dayRange(tailFrom, to) })
						: t("accountant.openPeriod.titleNone")}</strong>
					<span>{t(closedThrough ? "accountant.openPeriod.text" : "accountant.openPeriod.textNone")}</span>
					{serverTail?.note && <span className="tail-note">{serverTail.note}</span>}
				</div>
				{tailAmount && <div className="tail-amount"><b>{money(tailAmount.amount)}</b><span>{t("accountant.openPeriod.notBilled", { count: tailAmount.count })}</span></div>}
			</OpenPeriod>}
			{(deferred || unsettled || lateSettled || showNotBilled) && <DeferredSection aria-label={t("accountant.deferredTitle")}>
				<SectionTitle as="div" style={{ marginTop: 0 }}><h2>{t("accountant.deferredTitle")}</h2><span>{t("accountant.deferredSubtitle")}</span></SectionTitle>
				{mismatch && <div className="deferred-alert">
					<TriangleAlert size={20} aria-hidden="true" />
					<div><strong>{t("accountant.turnoverMismatch", { amount: money(mismatchAmount) })}</strong>{t("accountant.turnoverMismatchDescription")}</div>
				</div>}
				<div className="deferred-grid">
					{(deferred || lateSettled) && (["out", "in"] as const).map(direction => {
						const amounts = Object.entries(direction === "out" ? outAmounts : deferred?.in ?? {}).sort(([a], [b]) => a.localeCompare(b));
						const directionStatements = direction === "out" ? deferred?.outStatements ?? [] : deferred?.inStatements ?? [];
						return <div className={`deferred-card ${mismatch ? "muted" : direction}`} key={direction}>
							<header><h4>{t(direction === "out" ? "accountant.deferredOut" : "accountant.deferredIn")}</h4>{!(direction === "out" && lateSettled) && <span className="deferred-count">{t("accountant.deferredCount", { count: direction === "out" ? deferred?.outCount ?? 0 : deferred?.inCount ?? 0 })}</span>}</header>
							<p>{t(direction === "out" ? "accountant.deferredOutDescription" : "accountant.deferredInDescription")} <strong>{t(direction === "out" ? "accountant.deferredOutHint" : "accountant.deferredInHint")}</strong></p>
							{amounts.length ? <dl>{amounts.map(([code, amount]) => <div key={code}>
								<dt>{mismatch ? t("accountant.deferredAtLeast", { currency: code }) : code}</dt><dd>{formatProfitAmount(amount, code, locale)}</dd>
							</div>)}</dl> : <div className="deferred-empty">{t("accountant.deferredNothing", { amount: money(0) })}</div>}
							{mismatch && !!amounts.length && <div className="deferred-pill">{t("accountant.deferredLowerBound")}</div>}
							{!!directionStatements.length && <>
								<hr />
								<ul className="deferred-list">{directionStatements.map(statement => <li key={statement.id}>
									<button type="button" className="deferred-entry" aria-label={t("accountant.openStatement", { id: statement.id })} onClick={() => S_STATEMENT_POPUP.invoke({ id: statement.id })}>
										<span className="deferred-entry-row"><span className="deferred-entry-id">{t("accountant.statement")} #{statement.id}</span><span className="deferred-entry-meta">{statement.type} · {statement.status}</span></span>
										<span className="deferred-entry-row"><span>{statement.periodFrom || "—"} → {statement.periodTo || "—"}</span><span className="deferred-entry-meta">{t("accountant.deferredCount", { count: statement.count })}</span></span>
										<span className="deferred-entry-amount">{Object.entries(statement.amount).sort(([a], [b]) => a.localeCompare(b)).map(([code, amount]) => <span key={code}>{formatProfitAmount(amount, code, locale)}</span>)}</span>
									</button>
								</li>)}</ul>
							</>}
							{direction === "out" && !!billedTransactions.length && <><hr /><ul className="deferred-list">{billedTransactions.map(transaction => renderUnsettledEntry(transaction, "open"))}</ul></>}
							{direction === "out" && lateSettled?.truncated && <span className="deferred-more">{t("accountant.lateSettledTruncated")}</span>}
						</div>;
					})}
					{unsettled && split && <div className={`deferred-card not-settled split ${split.stuck.count ? "unsettled" : "in"}`}>
						<header>
							<h4>{!!split.stuck.count && <OctagonAlert size={18} aria-hidden="true" />}{t(split.stuck.count ? "accountant.notSettled.title.stuck" : "accountant.notSettled.title.empty")}</h4>
							<span className="deferred-count">{t("accountant.deferredCount", { count: split.stuck.count })}</span>
						</header>
						<p><Trans t={t} i18nKey={split.stuck.count ? "accountant.notSettled.stuckText" : "accountant.notSettled.recentOnlyText"} components={{ b: <strong /> }} /></p>
						{split.stuck.count ? <dl className="settlement-rows"><div className="lost">
							<dt>{t("accountant.notSettled.stuckRow")}</dt>
							<dd>{amountsOf(split.stuck.byCurrency).map(item => <span key={item.code}>{formatProfitAmount(item.amount, item.code, locale)}</span>)}</dd>
						</div></dl> : <div className="deferred-empty">{t("accountant.notSettled.nothing", { amount: money(0) })}</div>}
						{!!stuckTransactions.length && <>
							<hr />
							<ul className="deferred-list">{stuckTransactions.map(transaction => renderUnsettledEntry(transaction, "lost"))}</ul>
						</>}
						{unsettled.truncated && <span className="deferred-more">{t("accountant.unsettledShown", { shown: unsettled.transactions.length, total: unsettled.total })}</span>}
						{stuckAfter !== undefined && <span className="deferred-more">{t(unsettled.stuckAfterSource === "client" ? "accountant.notSettled.thresholdClient" : "accountant.notSettled.threshold", { count: stuckAfter })}</span>}
						{!!split.recent.count && <div className="recent">
							<header><strong>{t("accountant.notSettled.recentTitle")}</strong><span className="deferred-count">{t("accountant.deferredCount", { count: split.recent.count })}</span></header>
							<div className="recent-row">
								<span>{stuckAfter !== undefined ? t("accountant.notSettled.recentText", { count: stuckAfter }) : t("accountant.notSettled.recentTextPlain")}</span>
								<b>{amountsOf(split.recent.byCurrency).map(item => <span key={item.code}>{formatProfitAmount(item.amount, item.code, locale)}</span>)}</b>
							</div>
							{!!recentTransactions.length && <details>
								<summary>{t("accountant.notSettled.recentShow", { count: recentTransactions.length })}</summary>
								<ul className="deferred-list">{recentTransactions.map(transaction => renderUnsettledEntry(transaction, "open"))}</ul>
							</details>}
						</div>}
					</div>}
					{unsettled && !split && <div className={`deferred-card not-settled ${notSettledState === "empty" ? "in" : "late"}`}>
						<header>
							<h4>
								{notSettledState !== "empty" && <Hourglass size={17} aria-hidden="true" />}
								{t(`accountant.notSettled.title.${notSettledState}`)}
							</h4>
							<span className="deferred-count">{t("accountant.deferredCount", { count: unsettled.total })}</span>
						</header>
						<p><Trans t={t} i18nKey={`accountant.notSettled.text.${notSettledState}`} components={{ b: <strong /> }} /></p>
						{notSettledRows.length ? <dl className={`settlement-rows${notSettledRows.length > 1 ? " boxed" : ""}`}>{notSettledRows.map(row => <div key={row.status} className="late">
							<dt>{statusLabel(row.status)}</dt>
							<dd>{row.amounts.map(item => <span key={item.code}>{formatProfitAmount(item.amount, item.code, locale)}</span>)}</dd>
						</div>)}</dl> : <div className="deferred-empty">{t("accountant.notSettled.nothing", { amount: money(0) })}</div>}
						{!!unsettled.transactions.length && <>
							<hr />
							<ul className="deferred-list">{unsettled.transactions.map(transaction => renderUnsettledEntry(transaction, settlementGroup(transaction.status)))}</ul>
							{unsettled.truncated && <span className="deferred-more">{t("accountant.unsettledShown", { shown: unsettled.transactions.length, total: unsettled.total })}</span>}
						</>}
					</div>}
					{showNotBilled && <div className={`deferred-card not-billed${hasNotBilled ? " has-unbilled" : ""}`}>
						<header><h4>{hasNotBilled && <OctagonAlert size={18} aria-hidden="true" />}{t("accountant.notBilled")}</h4></header>
						<p>{t("accountant.notBilledDescription")}</p>
						{hasNotBilled ? <dl>{Object.entries(notPaidOut?.notBilled ?? {}).sort(([a], [b]) => a.localeCompare(b)).map(([code, amount]) => <div key={code}><dt>{code}</dt><dd>{formatProfitAmount(amount, code, locale)}</dd></div>)}</dl> : <div className="deferred-empty">{t("accountant.deferredNothing", { amount: money(0) })}</div>}
						{!!notBilledTransactions.length && <ul className="deferred-list">{notBilledTransactions.map(transaction => renderUnsettledEntry(transaction, "lost"))}</ul>}
						{lateSettled?.truncated && <span className="deferred-more">{t("accountant.lateSettledTruncated")}</span>}
					</div>}
				</div>
				{mismatch && (notPaidOut || unsettled) && <div className="not-paid-out withheld"><Trans t={t} i18nKey="accountant.notPaidOutWithheld" components={{ b: <strong /> }} /></div>}
				{reconciled && notPaidOutTotal !== undefined && <div className={`not-paid-out${notPaidOutTotal ? "" : " calm"}`}>
						<span className="not-paid-out-label">{t("accountant.notPaidOutTotal")}</span>
						<span className="not-paid-out-value"><Trans t={t} i18nKey={showNotBilled ? "accountant.notPaidOutBreakdownWithNotBilled" : "accountant.notPaidOutBreakdown"} values={{ notBilled: plain(notBilledAmount), deferred: plain(deferredOut), unsettled: plain(unsettledAmount), total: money(notPaidOutTotal) }} components={{ b: <strong /> }} /></span>
					</div>}
				{deferred?.note && <p className="deferred-note"><Info size={18} aria-hidden="true" /><span>{deferred.note}</span></p>}
			</DeferredSection>}
			{byPeriod && <section aria-label={t("accountant.byReportDays")}>
				<SectionTitle><h2>{t("accountant.byReportDays")}</h2><span>{t("accountant.byReportDaysHint", { count: data.accrued.daysCovered })}</span></SectionTitle>
				{/* The settlement shift is the caption of this block, not a figure of its own: it explains why it differs from the blocks above. */}
				{reportedLater && reportedEarlier && (reportedLater.amount !== 0 || reportedEarlier.amount !== 0) && <ShiftCaption aria-label={t("accountant.shiftTitle")}>
					<span className="shift-text"><Trans t={t} i18nKey={overview ? "accountant.shift.caption" : "accountant.shift.captionShort"} values={{ here: plain(insideTurnover), above: plain(overview?.amount ?? 0) }} components={{ b: <strong /> }} /></span>
					<span className="shift-sides">
						{[
							{ key: "in", value: reportedEarlier, note: data.reportedEarlier?.note, day: shiftDay(from, -1) },
							{ key: "out", value: reportedLater, note: data.reportedLater?.note, day: shiftDay(to, 1) },
						].map(side => <span key={side.key} title={side.note}>
							{from === to ? t(`accountant.shift.${side.key}Day`, { day: side.day }) : t(`accountant.shift.${side.key}Range`)}
							{" "}<strong>{plain(side.value.amount)}</strong> <em>· {new Intl.NumberFormat(locale).format(side.value.count)}</em>
						</span>)}
					</span>
				</ShiftCaption>}
				<TableScroll>
					<table aria-label={t("accountant.byReportDays")}><thead><tr><th>{t("accountant.period")}</th><th>{t("accountant.turnover")}</th><th>{t("accountant.fees")}</th><th>{t("accountant.rollingTaken")}</th><th>{t("accountant.rollingReleased")}</th><th>{t("accountant.refunds")}</th><th>{t("accountant.chargebacks")}</th><th>{t("accountant.net")}</th></tr></thead>
					{/* Zero is printed as 0.00 and "no dates outside": a dash would read as unknown where the truth is none. */}
					<tbody>{periodRows.map(row => <tr key={row.key} className={row.key === "outside" && !byPeriod.outsideDays.length ? "muted" : undefined}>
						<td>{row.label}{row.key === "outside" && <span className="row-hint"> · {byPeriod.outsideDays.length
							? t("accountant.outsideDaysList", { count: byPeriod.outsideDays.length, days: byPeriod.outsideDays.join(", ") })
							: t("accountant.noOutsideDays")}</span>}</td>
						{renderChannelMetrics(row.metrics ?? zeroMetrics)}
					</tr>)}</tbody></table>
				</TableScroll>
			</section>}
			<SectionHeading><span>{t("accountant.earningsByChannel")}</span><KpiLabel>{t("accountant.channels", { count: channels.length })}</KpiLabel></SectionHeading>
			{channels.length ? <TableScroll>
				<table aria-label={t("accountant.earningsByChannel")}><thead><tr><th>{t("accountant.channel")}</th><th>{t("accountant.turnover")}</th><th>{t("accountant.fees")}</th><th>{t("accountant.rollingTaken")}</th><th>{t("accountant.rollingReleased")}</th><th>{t("accountant.refunds")}</th><th>{t("accountant.chargebacks")}</th><th>{t("accountant.net")}</th></tr></thead>
				<tbody>
					{channels.map(channel => <tr key={channel.id}><td>{channel.name}</td>{renderChannelMetrics(channel.currencies[currency])}</tr>)}
					{channels.length > 1 && channelTotal && <tr className="total"><td>{t("accountant.total")}</td>{renderChannelMetrics(channelTotal)}</tr>}
				</tbody></table>
			</TableScroll> : <KpiLabel style={{ display: "block", marginTop: 10 }}>{t("accountant.channelsEmpty")}</KpiLabel>}
			{view === "paid" && <>
				<SectionHeading><span>{t("accountant.statements")}</span><KpiLabel>{pendingAmount ? t("accountant.pendingAmount", { amount: money(pendingAmount) }) : t("accountant.pendingEmpty")}</KpiLabel></SectionHeading>
				{statements.length ? <TableScroll>
					<table aria-label={t("accountant.statements")}><thead><tr><th>{t("accountant.statement")}</th><th>{t("accountant.type")}</th><th>{t("accountant.status")}</th><th>{t("accountant.period")}</th><th>{t("accountant.coverage")}</th><th>{t("accountant.net")}</th></tr></thead>
					<tbody>{statements.map(statement => <tr key={statement.id} data-clickable onClick={() => S_STATEMENT_POPUP.invoke({ id: statement.id })}>
						<td><button type="button" className="statement-open" title={t("accountant.openStatement", { id: statement.id })} aria-label={t("accountant.openStatement", { id: statement.id })}>{statement.id}</button></td>
						<td>{statement.type || "—"}</td><td>{statement.status}</td><td>{statement.periodFrom} → {statement.periodTo}</td><td><StatusBadge $partial={!statement.fullyInsidePeriod}>{t(statement.fullyInsidePeriod ? "accountant.fullyCovered" : "accountant.partiallyCovered")}</StatusBadge></td><td>{money(statement.net[currency])}</td>
					</tr>)}</tbody></table>
				</TableScroll> : <KpiLabel style={{ display: "block", marginTop: 10 }}>{t("accountant.statementsEmpty")}</KpiLabel>}
			</>}
			{reconciled && metrics && <Reconciled>
				<CircleCheck size={20} aria-hidden="true" />
				<div>
					<strong>{t("accountant.reconciledTitle")}</strong>
					{overviewTurnover !== undefined && <p>
						{plain(overviewTurnover)} <em>{t("accountant.formula.created")}</em>
						{" − "}{plain(deferredOut)} <em>{t("accountant.formula.deferred")}</em>
						{" − "}{plain(unsettledAmount)} <em>{t("accountant.formula.notSettled")}</em>
						{!!notBilledAmount && <>{" − "}{plain(notBilledAmount)} <em>{t("accountant.notBilled")}</em></>}
						{!!deferredIn && <>{" + "}{plain(deferredIn)} <em>{t("accountant.formula.earlier")}</em></>}
						{" = "}<b>{plain(metrics.turnover)} {t("accountant.formula.byDocuments")}</b>
					</p>}
					{/* by documents − out of the reports + into the reports + deferred.out − deferred.in − beyond = by report days */}
					{reportedLater && reportedEarlier && byPeriod && <p>
						{plain(metrics.turnover)} <em>{t("accountant.formula.byDocuments")}</em>
						{" − "}{plain(reportedLater.amount)} <em>{t("accountant.formula.outOfReports")}</em>
						{" + "}{plain(reportedEarlier.amount)} <em>{t("accountant.formula.intoReports")}</em>
						{!!deferredOut && <>{" + "}{plain(deferredOut)} <em>{t("accountant.formula.deferred")}</em></>}
						{!!deferredIn && <>{" − "}{plain(deferredIn)} <em>{t("accountant.formula.earlier")}</em></>}
						{!!deferredBeyond && <>{" − "}{plain(deferredBeyond)} <em>{t("accountant.formula.beyond")}</em></>}
						{" = "}<b>{plain(insideTurnover)} {t("accountant.formula.byReportDays")}</b>
					</p>}
				</div>
			</Reconciled>}
			<Reconciliation $warning={data.reconciliation.uncoveredDays > 0}>
				<strong><CircleAlert size={15} aria-hidden="true" style={{ verticalAlign: -3, marginRight: 7 }} />{data.reconciliation.uncoveredDays > 0 ? t("accountant.uncoveredDays", { count: data.reconciliation.uncoveredDays }) : t("accountant.fullyReconciled")}</strong>
				{!!data.reconciliation.notes.length && <ul>{data.reconciliation.notes.map(note => <li key={note}>{note}</li>)}</ul>}
			</Reconciliation>
		</>}
	</Card>;
};
